import React from 'react';
import { Text, Link, LinkProps } from '@perch-labs/shrike';
import { formatDate } from '@perch-labs/warbler';
import { useMutation } from '@apollo/client';
import Markdown from 'markdown-to-jsx';
import { copyFor } from 'config';
import { Container } from 'styles';
import { TextProps, ButtonProps } from 'types/style';
import {
  KEYS_FOR_NOTIFICATIONS_WITH_LOVEBIRD_LINKS,
  NotificationKey,
} from 'domain/notifications';
import { trackEvent } from 'domain/tracking';
import { DISMISS_NOTIFICATION } from './notification.api';
import { Action } from './action';
import { StyledDiv, StyledIconButton, StyledText } from './notification.style';

const getCopy = copyFor('modules.notification');
type LinkColor = Pick<LinkProps, 'color'>['color'];
type ButtonColor = Pick<ButtonProps, 'color'>['color'];
export type ButtonLinkColor = Extract<LinkColor, ButtonColor>;
export type ButtonLinkProps = Pick<ButtonProps, 'backgroundless' | 'margin'> & {
  color?: ButtonLinkColor;
};
const NOTIFICATION_KEY_TURN_OFF_AUTOPAY = 'turn_off_autopay';
const NOTIFICATION_KEY_OR_LOW_INCOME_USER = 'or_low_income_customer';
const OR_COM_SOLAR_URL = 'https://www.oregoncsp.org/li/';

interface Props {
  notification: QueriedNotification;
  buttonLinkProps?: ButtonLinkProps;
  titleProps?: TextProps;
  messageProps?: TextProps;
  showDismissButton?: boolean;
  showDate?: boolean;
  shouldShowSubtitle: boolean;
}

export const Notification = ({
  notification,
  buttonLinkProps,
  titleProps,
  messageProps,
  showDismissButton,
  showDate = true,
  shouldShowSubtitle,
}: Props) => {
  const {
    createdAt,
    displayValues,
    notificationKey,
    targetId,
    id,
    utilityAccountDetails,
  } = notification;
  const { title, message } = displayValues;
  const { accountNumber, displayServiceAddress, utilityDisplayName } =
    utilityAccountDetails || {};
  const subtitle = shouldShowSubtitle
    ? `${utilityDisplayName} ${accountNumber ? `#${accountNumber}` : ''} ${
        displayServiceAddress ? ` • ${displayServiceAddress}` : ''
      }`
    : '';
  const [mutate, { loading: dismissLoading }] =
    useMutation<DismissDashboardNotificationMutation>(DISMISS_NOTIFICATION);
  const dismissNotification = async () => {
    await mutate({
      variables: {
        input: { active: false, id },
      },
    });

    if (notificationKey === NOTIFICATION_KEY_TURN_OFF_AUTOPAY) {
      trackEvent('track_autopay_dismissal', {
        metadata: { utilityCredentialId: targetId },
      });
    }
  };

  const openInNewTab = !KEYS_FOR_NOTIFICATIONS_WITH_LOVEBIRD_LINKS.includes(
    notificationKey as NotificationKey
  );

  return (
    <>
      <Container>
        <StyledText tag="h3" textStyle="heading500" {...titleProps}>
          {title}
        </StyledText>
        {shouldShowSubtitle && (
          <Text
            data-testid="subtitle"
            textStyle="paragraph400"
            color="secondary"
            margin={{ top: '8px' }}
          >
            {subtitle}
          </Text>
        )}
        <Text margin={{ top: '8px' }} {...messageProps}>
          {notificationKey !== NOTIFICATION_KEY_OR_LOW_INCOME_USER ? (
            message && (
              <Markdown
                options={{
                  overrides: {
                    a: {
                      component: Link,
                      props: {
                        openInNewTab,
                        type: 'inline',
                      },
                    },
                  },
                }}
              >
                {message}
              </Markdown>
            )
          ) : (
            <>
              Eligible low-income users may qualify for extra savings with
              Oregon's Community Solar Program. Visit{' '}
              <Link
                openInNewTab={openInNewTab}
                type="inline"
                href={OR_COM_SOLAR_URL}
                onClick={() => {
                  trackEvent('track_oregon_lmi_link_clicked', {
                    metadata: {
                      utilityCredentialId: targetId,
                      clicked: 'oregon_csp_website',
                    },
                  });
                }}
              >
                www.oregoncsp.org/li/
              </Link>
              , call 971 544 8718, or email{' '}
              <Link
                openInNewTab={openInNewTab}
                type="inline"
                href="mailto:solar@communityenergyproject.org"
                onClick={() => {
                  trackEvent('track_oregon_lmi_link_clicked', {
                    metadata: {
                      utilityCredentialId: targetId,
                      clicked: 'support_email',
                    },
                  });
                }}
              >
                solar@communityenergyproject.org
              </Link>{' '}
              for details.
            </>
          )}
        </Text>
        {showDate && (
          <Text
            margin={{ top: '8px' }}
            color="secondary"
            textStyle="paragraph400"
          >
            {formatDate(createdAt, { format: 'MMMM d, yyyy' })}
          </Text>
        )}
        <Action
          notification={notification}
          buttonLinkProps={buttonLinkProps}
          dismissNotification={dismissNotification}
        />
      </Container>
      {showDismissButton && (
        <StyledDiv>
          <StyledIconButton
            aria-label={getCopy('dismiss')}
            icon="UIClose"
            onClick={() => dismissNotification()}
            loading={dismissLoading}
          />
        </StyledDiv>
      )}
    </>
  );
};
